import { Component } from '@theme/component';

class TrendingCarousel extends Component {
  requiredRefs = ['swiper', 'modalContainer'];

  #swiper = null;
  #previousScrollY = 0;
  #videoObserver = null;
  #blockIds = [];

  connectedCallback() {
    super.connectedCallback();

    this.#blockIds = [...this.querySelectorAll('.trending-carousel-item__modal-template')].map(
      (template) => template.dataset.blockId
    );

    this.#initSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#videoObserver?.disconnect();
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #initSwiper() {
    if (!this.refs.swiper) {
      return;
    }

    this.#swiper = new window.Swiper(this.refs.swiper, {
      loop: true,
      allowTouchMove: true,
      slidesPerView: 1.2,
      spaceBetween: 20,

      breakpoints: {
        750: {
          slidesPerView: 3,
          centeredSlides: true,
        },
      },
      on: {
        afterInit: () => this.#observeSlideVideos(),
        slidesUpdated: () => this.#observeSlideVideos(),
      },
    });
  }

  /**
   * Hydrate a hosted video by moving data-src onto source elements.
   *
   * @param {HTMLVideoElement | null} video
   */
  #hydrateVideo(video) {
    if (!(video instanceof HTMLVideoElement) || video.dataset.srcReady === 'true') {
      return;
    }

    const sources = video.querySelectorAll('source[data-src]');

    if (!sources.length) {
      return;
    }

    sources.forEach((source) => {
      source.src = source.dataset.src;
    });

    video.load();
    video.dataset.srcReady = 'true';
  }

  #observeSlideVideos() {
    if (!this.#videoObserver) {
      this.#videoObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#hydrateVideo(entry.target);
            }
          }
        },
        { root: null, rootMargin: '80px 80px', threshold: 0 }
      );
    }

    this.querySelectorAll('.swiper-slide .trending-carousel-item__video').forEach((video) => {
      this.#videoObserver.observe(video);
    });
  }

  /**
   * Stamp a modal from its inert template the first time it is needed.
   *
   * @param {string} blockId
   * @returns {HTMLElement | null}
   */
  #ensureModal(blockId) {
    const existingModal = this.refs.modalContainer.querySelector(`[data-block-id="${blockId}"]`);

    if (existingModal) {
      return existingModal;
    }

    const template = this.querySelector(
      `template.trending-carousel-item__modal-template[data-block-id="${blockId}"]`
    );

    if (!(template instanceof HTMLTemplateElement)) {
      return null;
    }

    this.refs.modalContainer.append(template.content.cloneNode(true));

    return this.refs.modalContainer.querySelector(`[data-block-id="${blockId}"]`);
  }

  slideNext() {
    this.#swiper.slideNext();
  }

  slidePrev() {
    this.#swiper.slidePrev();
  }

  /**
   * Show the modal.
   *
   * @param {PointerEvent} event
   */
  showModal(event) {
    const button = event.target;
    const modal = this.#ensureModal(button.dataset.blockId);

    if (!modal) {
      return;
    }

    modal.classList.add('trending-carousel-item__modal-content--open');
    this.#hydrateVideo(modal.querySelector('video'));
    this.pauseVideos();
    this.#lockBodyScroll();
  }

  closeModal() {
    const modal = this.refs.modalContainer.querySelector('.trending-carousel-item__modal-content--open');
    modal?.classList.remove('trending-carousel-item__modal-content--open');
    modal?.querySelector('video')?.pause();

    this.#unlockBodyScroll();
  }

  showNextModal() {
    const currentModal = this.refs.modalContainer.querySelector('.trending-carousel-item__modal-content--open');
    const nextModal = this.#modalByOffset(currentModal, 1);

    if (!currentModal || !nextModal) {
      return;
    }

    this.#switchVisibleModal(currentModal, nextModal);
  }

  showPrevModal() {
    const currentModal = this.refs.modalContainer.querySelector('.trending-carousel-item__modal-content--open');
    const prevModal = this.#modalByOffset(currentModal, -1);

    if (!currentModal || !prevModal) {
      return;
    }

    this.#switchVisibleModal(currentModal, prevModal);
  }

  /**
   * @param {Element | null} currentModal
   * @param {number} offset
   * @returns {HTMLElement | null}
   */
  #modalByOffset(currentModal, offset) {
    if (!currentModal || !this.#blockIds.length) {
      return null;
    }

    const currentIndex = this.#blockIds.indexOf(currentModal.dataset.blockId);
    const nextIndex = (currentIndex + offset + this.#blockIds.length) % this.#blockIds.length;

    return this.#ensureModal(this.#blockIds[nextIndex]);
  }

  toggleVideoPlay(event) {
    const parent = event.target.parentNode;
    const video = parent.querySelector('video');

    if (!video) {
      return;
    }

    this.#hydrateVideo(video);

    if (video.paused) {
      video.loop = true;
      video.muted = false;
      video.play();

      this.#togglePlayIcons(event.target, true);
      this.pauseVideos(video);
    } else {
      video.pause();

      this.#togglePlayIcons(event.target, false);
    }
  }

  /**
   * Loop through all of the video elements in the carousel and pause anything
   * that should not be currently playing.
   *
   * @param {HTMLVideoElement | null} excludedVideo The video that is currently playing.
   */
  pauseVideos(excludedVideo = null) {
    const trendingCarousel = this.closest('trending-carousel');
    const allVideos = trendingCarousel?.querySelectorAll('video');

    if (!allVideos) {
      return;
    }

    [...allVideos]
      ?.filter((video) => !excludedVideo || (!video.isSameNode(excludedVideo) && !video.paused))
      .forEach((video) => {
        video.pause();

        const button = video
          .closest('.trending-carousel-item, .trending-carousel-item__modal__image-column')
          ?.querySelector('.trending-carousel-item__play');
        this.#togglePlayIcons(button, false);
      });
  }

  #togglePlayIcons(parent, isPlaying) {
    if (!parent) {
      return;
    }

    const playIcon = parent.querySelector('.icon-play-button__play');
    const pauseIcon = parent.querySelector('.icon-play-button__pause');

    playIcon?.classList.toggle('icon-play-button--hidden', isPlaying);
    pauseIcon?.classList.toggle('icon-play-button--hidden', !isPlaying);
  }

  #switchVisibleModal(currentModal, nextModal) {
    currentModal.classList.remove('trending-carousel-item__modal-content--open');
    currentModal.querySelector('video')?.pause();
    nextModal.classList.add('trending-carousel-item__modal-content--open');
    this.#hydrateVideo(nextModal.querySelector('video'));
  }

  #lockBodyScroll() {
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    });
  }

  #unlockBodyScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }
}

if (!customElements.get('trending-carousel')) {
  customElements.define('trending-carousel', TrendingCarousel);
}
